import { clearLoggedOutSessionActivity } from "@faire/retailer-visitor-shared/actions/user/recordLoggedOutActivity";
import {
  BUSINESS_TYPE,
  BUSINESS_TYPE_REVAMP,
} from "@faire/retailer-visitor-shared/consts/BUSINESS_TYPE";
import { SignUp } from "@faire/retailer-visitor-shared/events";
import { SignUpRequestExpanded } from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { useFaireDirectBrand } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { useSetUser } from "@faire/retailer-visitor-shared/stores/domain/useUserUpdate";
import { useTrackSignUpSubmission } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/__internal__/useSignUpSubmissionTracker";
import { useHumanReadableErrorMessage } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useHumanReadableErrorMessage";
import { useSignUpQueryParams } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpQueryParams";
import { saveCredentialsInBrowser } from "@faire/retailer-visitor-shared/views/SignUp/_utils/credentialsUtils";
import { getPreSignUpActivities } from "@faire/retailer-visitor-shared/views/SignUp/_utils/getPreSignUpActivities";
import { useSignUpRequest } from "@faire/retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { useAllFieldsOfContextStore } from "@faire/web/ui/hooks/ContextStore/hooks";
import { useMutation as useCreateUserWebApi } from "@faire/web-api--source/endpoints/www/api/user/sign-up/post-hooks";
import { ISignUpRequest } from "@faire/web-api--source/indigofair/data/ISignUpRequest";
import { ISignUpResponse } from "@faire/web-api--source/indigofair/data/ISignUpResponse";
import { useCallback, useState } from "react";

import BusinessPlatform = ISignUpRequest.BusinessPlatform;

export const useCreateUserAndRetailer = () => {
  const { buildSignUpPayload } = useBuildSignUpRequest();
  const { signUp } = useSignUp();
  const { handlePostSignUp } = useHandlePostSignUp();
  const { humanReadableErrorMessage } = useHumanReadableErrorMessage();
  const {
    trackFailure: trackSubmissionFailure,
    trackStart: trackSubmissionStart,
    trackSuccess: trackSubmissionSuccess,
  } = useTrackSignUpSubmission();

  const [isLoading, setLoading] = useState(false);
  const [userErrorMessage, setUserErrorMessage] = useState<
    string | undefined
  >();

  const createUserAndRetailer = useCallback(
    async (
      values?: Partial<SignUpRequestExpanded>
    ): Promise<ISignUpResponse | undefined> => {
      try {
        setLoading(true);

        trackSubmissionStart();
        const payload = buildSignUpPayload(values);
        const response = await signUp(payload);
        trackSubmissionSuccess();

        handlePostSignUp(payload, response);

        return response;
      } catch (error) {
        trackSubmissionFailure(error);
        setUserErrorMessage(humanReadableErrorMessage(error));
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [
      trackSubmissionStart,
      buildSignUpPayload,
      signUp,
      trackSubmissionSuccess,
      handlePostSignUp,
      trackSubmissionFailure,
      humanReadableErrorMessage,
    ]
  );

  return { createUserAndRetailer, userErrorMessage, isLoading };
};

export const useBuildSignUpRequest = () => {
  const { queryParams, isFaireDirectCreditEligible } = useSignUpQueryParams();
  const [signUpRequest] = useAllFieldsOfContextStore(useSignUpRequest);
  const { isActive: isSSOActive, ssoToken } = useSSOStore();
  const { isFaireDirectActiveInLoggedOut, faireDirectBrandAlias } =
    useFaireDirectBrand();

  const buildSignUpPayload = useCallback(
    (partialRequest?: Partial<SignUpRequestExpanded>): ISignUpRequest => {
      const mergedRequest = { ...signUpRequest, ...partialRequest };
      const businessPlatforms = [...mergedRequest.business_platforms];
      // Replace OTHER_PLATFORM with the user's description before sending
      const indexOtherPlatform = businessPlatforms.indexOf(
        ISignUpRequest.BusinessPlatform.OTHER_PLATFORM
      );
      if (
        indexOtherPlatform !== -1 &&
        mergedRequest.other_business_platform_description
      ) {
        businessPlatforms.splice(
          indexOtherPlatform,
          1,
          mergedRequest.other_business_platform_description
        );
        mergedRequest.business_platforms = businessPlatforms;
      }
      const businessPlatformType =
        BUSINESS_TYPE_REVAMP[
          (mergedRequest.business_type ??
            BusinessPlatform.BUSINESS_PLATFORM_UNKNOWN) as BusinessPlatform
        ];

      const business_type =
        businessPlatformType === BUSINESS_TYPE.OTHER
          ? mergedRequest.other_business_platform_description
          : businessPlatformType;

      const payload = ISignUpRequest.build({
        ...mergedRequest,
        business_type,
        widget_token: queryParams.widgetToken,
        pre_sign_up_activities: getPreSignUpActivities(queryParams.source),
        sso_token: isSSOActive ? ssoToken : undefined,
        faire_direct_brand_alias:
          isFaireDirectActiveInLoggedOut && isFaireDirectCreditEligible
            ? faireDirectBrandAlias
            : undefined,
      });

      if (payload.postal_code === "") {
        payload.postal_code = undefined;
      }

      if (isSSOActive) {
        delete payload.user?.password;
      }

      return payload;
    },
    [
      isFaireDirectCreditEligible,
      faireDirectBrandAlias,
      isSSOActive,
      isFaireDirectActiveInLoggedOut,
      queryParams.source,
      queryParams.widgetToken,
      signUpRequest,
      ssoToken,
    ]
  );

  return { buildSignUpPayload };
};

const useSignUp = () => {
  const { queryParams } = useSignUpQueryParams();
  const { mutateAsync: createUserWebApi } = useCreateUserWebApi();

  const signUp = useCallback(
    async (payload: ISignUpRequest): Promise<ISignUpResponse> => {
      const response = await createUserWebApi([
        payload,
        { brand_invoice_token: queryParams.brandInvoice },
      ]);

      clearLoggedOutSessionActivity();

      return { ...response };
    },
    [createUserWebApi, queryParams.brandInvoice]
  );

  return { signUp };
};

const useHandlePostSignUp = () => {
  const { setRetailer } = useRetailer();
  const setUser = useSetUser();
  const { isActive: isSSOActive } = useSSOStore();

  const handlePostSignUp = useCallback(
    (request: ISignUpRequest, response: ISignUpResponse) => {
      const { retailer, user } = response;

      setRetailer(retailer);
      setUser(user);

      if (retailer && user) {
        SignUp.publish({ user, retailer });
        if (!isSSOActive) {
          saveCredentialsInBrowser(
            request.user?.email_address,
            request.user?.password
          );
        }
      }
    },
    [isSSOActive, setRetailer, setUser]
  );

  return { handlePostSignUp };
};
